import React from 'react';
import PropTypes from 'prop-types';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import Carousel from 'components/Carousel';
import { CARD_GAP, CARD_WIDTH } from 'constants/productCard';
import { Text } from 'components/ui';
import RichText from 'components/Content/RichText';
import Storage from 'utils/localStorage/Storage';
import contentConsts from 'constants/content';
import {
    colors, fontSizes, lineHeights, space
} from 'style/config';
import store from 'store/Store';
import Actions from 'Actions';
import authUtils from 'utils/Authentication';
import { HEADER_VALUE } from 'constants/authentication';
import anaUtils from 'analytics/utils';
import helpersUtils from 'utils/Helpers';

const {
    RECAP_CAROUSEL: { ITEM_URLS }
} = contentConsts;
const { deferTaskExecution } = helpersUtils;

class Recap extends BaseClass {
    controlReplenImpressionFired = false;
    controlAnonymousImpressionFired = false;

    componentDidMount() {
        const { requiredData = {} } = this.props;

        this.props.fetchPurchaseHistory(this.props);
        this.props.fetchBeautyRecommendations(this.props);
        requiredData.rvData && this.props.updateRvData(Storage.local.getItem('rvData'));
        this.fetchRtcoDataIfEnabled();
        this.maybeFireControlReplenImpression();
        this.maybeFireControlAnonymousImpression();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.userId !== this.props.userId) {
            this.props.fetchPurchaseHistory(this.props);
            this.props.fetchBeautyRecommendations(this.props);
            this.fetchRtcoDataIfEnabled();
            this.controlReplenImpressionFired = false;
            this.controlAnonymousImpressionFired = false;
        }

        this.maybeFireControlReplenImpression();
        this.maybeFireControlAnonymousImpression();

        if (prevProps.isAnonymous && !this.props.isAnonymous) {
            this.props.clearRecapAbTestData();
        }
    }
    maybeFireControlReplenImpression() {
        if (this.controlReplenImpressionFired) {
            return;
        }

        if (this.props.hasControlReplenImpression && this.props.triggerControlReplenImpression) {
            this.props.triggerControlReplenImpression();
            this.controlReplenImpressionFired = true;
        }
    }
    // PUWLO Unrecognized — control-group impression fires once per session
    // even though the carousel is hidden. Mirrors maybeFireControlReplenImpression
    // so anonymous Control bucket measurement matches Challenger.
    maybeFireControlAnonymousImpression() {
        if (this.controlAnonymousImpressionFired) {
            return;
        }

        if (this.props.hasControlAnonymousImpression && this.props.triggerControlAnonymousImpression) {
            this.props.triggerControlAnonymousImpression();
            this.controlAnonymousImpressionFired = true;
        }
    }

    fetchRtcoDataIfEnabled() {
        const basketItem = this.props?.items?.find(item => item?.action?.targetUrl === ITEM_URLS.BASKET);

        if (basketItem) {
            const { personalization } = basketItem;
            const isEnabled = personalization?.isEnabled;
            const isRtcoEnabled = personalization?.isRtcoEnabled;
            const context = personalization?.context;

            if (isEnabled && isRtcoEnabled && context) {
                this.props.fetchRtcoP13nData({ contextId: context });
            }
        }
    }

    handleSignInClick = e => {
        e.preventDefault();
        const { title, triggerSignInClick } = this.props;

        // PUWLO Unrecognized — fire SOT click event with anonymous AB-test
        // attributes (sotV439) before opening the sign-in
        // modal. The modal has its own analytics, so this fires in addition.
        if (typeof triggerSignInClick === 'function') {
            triggerSignInClick();
        }

        deferTaskExecution(() => {
            const navSegment = title ? title.toLowerCase() : 'sign-in';

            store.dispatch(
                Actions.showSignInModal({
                    isOpen: true,
                    source: authUtils.SIGN_IN_SOURCES.RECAP_SIGN_IN_CTA,
                    analyticsData: {
                        navigationInfo: anaUtils.buildNavPath(['content', 'recap', navSegment])
                    },
                    extraParams: { headerValue: HEADER_VALUE.USER_CLICK }
                })
            );
        });
    };
    render() {
        const {
            sid = null,
            cards = null,
            title = null,
            subtitle = null,
            hideRow = false,
            isLoading = true,
            triggerRecapImpression = null,
            isAnonymous = false,
            anonymousSubtitle = null
        } = this.props;

        if (hideRow) {
            return null;
        }

        return (
            <div id={sid}>
                {title && (
                    <Text
                        is='h2'
                        lineHeight='tight'
                        marginBottom={subtitle ? '.25em' : 4}
                        fontSize={['md', 'lg']}
                        fontWeight='bold'
                        children={title}
                    />
                )}
                {subtitle && (
                    <Text
                        is='p'
                        lineHeight='tight'
                        marginBottom={4}
                        marginRight='auto'
                        children={subtitle}
                    />
                )}
                {isAnonymous && anonymousSubtitle?.text && (
                    <div css={styles.anonymousSubtitle}>
                        <RichText
                            content={anonymousSubtitle.text}
                            linkColor={colors.blue}
                            onInlineSignInClick={this.handleSignInClick}
                        />
                    </div>
                )}
                <Carousel
                    isLoading={isLoading}
                    gap={CARD_GAP}
                    paddingY={4}
                    marginX='-container'
                    scrollPadding={[2, 'container']}
                    itemWidth={CARD_WIDTH}
                    items={cards}
                    hasShadowHack={true}
                    onImpression={triggerRecapImpression}
                />
            </div>
        );
    }
}

const styles = {
    anonymousSubtitle: {
        marginBottom: space[2],
        fontSize: fontSizes.base,
        lineHeight: lineHeights.tight,
        color: colors.black
    }
};

Recap.propTypes = {
    sid: PropTypes.string,
    cards: PropTypes.array,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    hideRow: PropTypes.bool,
    isLoading: PropTypes.bool,
    componentList: PropTypes.array,
    items: PropTypes.array,
    fetchRtcoP13nData: PropTypes.func,
    fetchPurchaseHistory: PropTypes.func,
    fetchBeautyRecommendations: PropTypes.func,
    updateRvData: PropTypes.func,
    requiredData: PropTypes.object,
    triggerRecapImpression: PropTypes.func,
    hasControlReplenImpression: PropTypes.bool,
    triggerControlReplenImpression: PropTypes.func,
    hasControlAnonymousImpression: PropTypes.bool,
    triggerControlAnonymousImpression: PropTypes.func,
    triggerSignInClick: PropTypes.func,
    isAnonymous: PropTypes.bool,
    anonymousSubtitle: PropTypes.object,
    clearRecapAbTestData: PropTypes.func
};

export default wrapComponent(Recap, 'Recap', true);
